import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "@/LandingPage";
import { AuthProvider, useAuth } from "@/app/AuthContext";
import { I18nProvider } from "@/app/I18nContext";
import AppShell from "@/app/AppShell";
import Login from "@/app/pages/Login";
import Register from "@/app/pages/Register";
import Dashboard from "@/app/pages/Dashboard";
import Journal from "@/app/pages/Journal";
import Brain from "@/app/pages/Brain";
import Proposals from "@/app/pages/Proposals";
import Memory from "@/app/pages/Memory";
import Integrations from "@/app/pages/Integrations";
import Onboarding from "@/app/pages/Onboarding";
import Docs from "@/app/pages/Docs";
import OnboardingBanner from "@/app/components/OnboardingBanner";
import { LegalIndex, LegalViewer } from "@/app/pages/Legal";
import Sessions from "@/app/pages/Sessions";
import NewSession from "@/app/pages/NewSession";
import SessionDetail from "@/app/pages/SessionDetail";
import NewTeamOneOne from "@/app/pages/NewTeamOneOne";
import TeamOneOneDetail from "@/app/pages/TeamOneOneDetail";
import MeetingTemplateEditor from "@/app/pages/MeetingTemplateEditor";
import MeetingTemplateBuilder from "@/app/pages/MeetingTemplateBuilder";
import MeetingTemplateImport from "@/app/pages/MeetingTemplateImport";
import MeetingCopilot from "@/app/pages/MeetingCopilot";
import Framework from "@/app/pages/Framework";
import Metrics from "@/app/pages/Metrics";
import MetricGroups, { MetricGroupBuilder, MetricGroupTrack, MetricGroupView, MetricGroupAssign } from "@/app/pages/MetricGroups";
import Surveys, { SurveyRunner, SurveyPrompt } from "@/app/pages/Surveys";
import Roadmap from "@/app/pages/Roadmap";
import Toolbox from "@/app/pages/Toolbox";
import Admin from "@/app/pages/Admin";
import Feedback from "@/app/pages/Feedback";
import WhatsNext from "@/app/pages/WhatsNext";
import ComingSoon from "@/app/pages/ComingSoon";
import Settings from "@/app/pages/Settings";
import Community from "@/app/pages/Community";
import UserProfile from "@/app/pages/UserProfile";
import Portfolio from "@/app/pages/Portfolio";
import SnapshotHistory from "@/app/pages/SnapshotHistory";
import Team from "@/app/pages/Team";
import AcceptInvite from "@/app/pages/AcceptInvite";
import Mandates from "@/app/pages/Mandates";
import MandateDetail from "@/app/pages/MandateDetail";
import FrameworksList from "@/app/pages/FrameworksList";
import FrameworkBuilder from "@/app/pages/FrameworkBuilder";
import FrameworkImport from "@/app/pages/FrameworkImport";
import Faq from "@/app/pages/Faq";
import Terms from "@/app/pages/Terms";
import Privacy from "@/app/pages/Privacy";
import ForgotPassword from "@/app/pages/ForgotPassword";
import ResetPassword from "@/app/pages/ResetPassword";
import "@/app/styles.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="tb-app">
        <div className="tb-loading">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell><SurveyPrompt />{children}</AppShell>;
}

function AppHome() {
  const { user } = useAuth();
  // Journal is a premium feature. Only admins + explicitly enabled users get it as home.
  if (user && (user.role === "admin" || user.features?.journal_enabled === true)) {
    return (
      <>
        <OnboardingBanner />
        <Journal />
      </>
    );
  }
  return (
    <>
      <OnboardingBanner />
      <Dashboard />
    </>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <Routes>
            {/* Landing */}
            <Route path="/" element={<LandingPage pageFile="index.html" />} />
            <Route path="/articles/stuck-after-building" element={<LandingPage pageFile="article-stuck-after-building.html" />} />
            <Route path="/articles/too-many-directions" element={<LandingPage pageFile="article-too-many-directions.html" />} />
            <Route path="/articles/bad-advice-everywhere" element={<LandingPage pageFile="article-bad-advice-everywhere.html" />} />
            <Route path="/articles/no-accountability" element={<LandingPage pageFile="article-no-accountability.html" />} />
            <Route path="/gifts/traction-reset-checklist" element={<LandingPage pageFile="gift-traction-reset-checklist.html" />} />
            <Route path="/gifts/weekly-focus-framework" element={<LandingPage pageFile="gift-weekly-focus-framework.html" />} />
            <Route path="/gifts/decision-filter-tool" element={<LandingPage pageFile="gift-decision-filter-tool.html" />} />
            <Route path="/gifts/accountability-tracker" element={<LandingPage pageFile="gift-accountability-tracker.html" />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/legal" element={<LegalIndex />} />
            <Route path="/legal/:slug" element={<LegalViewer />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* Auth */}
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password/:token" element={<PublicOnly><ResetPassword /></PublicOnly>} />

            {/* App */}
            <Route path="/app" element={<Protected><AppHome /></Protected>} />
            <Route path="/app/bookings" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/journal" element={<Protected><Journal /></Protected>} />
            <Route path="/app/brain" element={<Protected><Brain /></Protected>} />
            <Route path="/app/proposals" element={<Protected><Proposals /></Protected>} />
            <Route path="/app/memory" element={<Protected><Memory /></Protected>} />
            <Route path="/app/integrations" element={<Protected><Integrations /></Protected>} />
            <Route path="/app/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/app/docs" element={<Protected><Docs /></Protected>} />
            <Route path="/app/legal" element={<Protected><LegalIndex inApp /></Protected>} />
            <Route path="/app/legal/:slug" element={<Protected><LegalViewer inApp /></Protected>} />
            <Route path="/app/sessions" element={<Protected><Sessions /></Protected>} />
            <Route path="/app/sessions/new" element={<Protected><NewSession /></Protected>} />
            <Route path="/app/sessions/:id" element={<Protected><SessionDetail /></Protected>} />
            <Route path="/app/team-1-1/new" element={<Protected><NewTeamOneOne /></Protected>} />
            <Route path="/app/team-1-1/:id" element={<Protected><TeamOneOneDetail /></Protected>} />
            <Route path="/app/settings/templates/import" element={<Protected><MeetingTemplateImport /></Protected>} />
            <Route path="/app/settings/templates/build/:id" element={<Protected><MeetingTemplateBuilder /></Protected>} />
            <Route path="/app/settings/templates/build" element={<Protected><MeetingTemplateBuilder /></Protected>} />
            <Route path="/app/settings/templates" element={<Protected><MeetingTemplateEditor /></Protected>} />
            <Route path="/app/framework" element={<Protected><Framework /></Protected>} />
            <Route path="/app/metrics" element={<Protected><Metrics /></Protected>} />
            <Route path="/app/metric-groups" element={<Protected><MetricGroups /></Protected>} />
            <Route path="/app/metric-groups/new" element={<Protected><MetricGroupBuilder /></Protected>} />
            <Route path="/app/metric-groups/:gid" element={<Protected><MetricGroupBuilder /></Protected>} />
            <Route path="/app/metric-groups/:gid/track" element={<Protected><MetricGroupTrack /></Protected>} />
            <Route path="/app/metric-groups/:gid/view" element={<Protected><MetricGroupView /></Protected>} />
            <Route path="/app/metric-groups/:gid/assign" element={<Protected><MetricGroupAssign /></Protected>} />
            <Route path="/app/surveys" element={<Protected><Surveys /></Protected>} />
            <Route path="/app/surveys/:slug" element={<Protected><SurveyRunner /></Protected>} />
            <Route path="/app/roadmap" element={<Protected><Roadmap /></Protected>} />
            <Route path="/app/toolbox" element={<Protected><Toolbox /></Protected>} />
            <Route path="/app/toolbox/agents/meeting-copilot" element={<Protected><MeetingCopilot /></Protected>} />
            <Route path="/app/services" element={<Protected><ComingSoon slug="services" /></Protected>} />
            <Route path="/app/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/app/feedback" element={<Protected><Feedback /></Protected>} />
            <Route path="/app/whats-next" element={<Protected><WhatsNext /></Protected>} />
            <Route path="/app/offerings" element={<Protected><ComingSoon slug="offerings" /></Protected>} />
            <Route path="/app/solutions/*" element={<Protected><ComingSoon slug="offersServices" /></Protected>} />
            <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/app/community" element={<Protected><Community /></Protected>} />
            <Route path="/app/portfolio" element={<Protected><Portfolio /></Protected>} />
            <Route path="/app/u/:uid" element={<Protected><UserProfile /></Protected>} />
            <Route path="/app/snapshots/:kind" element={<Protected><SnapshotHistory /></Protected>} />
            <Route path="/app/team" element={<Protected><Team /></Protected>} />
            <Route path="/app/mandates" element={<Protected><Mandates /></Protected>} />
            <Route path="/app/mandates/:id" element={<Protected><MandateDetail /></Protected>} />
            <Route path="/app/frameworks" element={<Protected><FrameworksList /></Protected>} />
            <Route path="/app/frameworks/build" element={<Protected><FrameworkBuilder /></Protected>} />
            <Route path="/app/frameworks/import" element={<Protected><FrameworkImport /></Protected>} />
            <Route path="/app/frameworks/build/:id" element={<Protected><FrameworkBuilder /></Protected>} />

            {/* Accept team invite (public — handles both logged-in & logged-out states) */}
            <Route path="/team/accept/:token" element={<AcceptInvite />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
